// Wind particle shaders - screen-space trail buffer with camera-clear (v5)
//
// The UPDATE shader has always correctly followed wind barbs in UV space.
// The only problem was the screen-space trail smearing on camera pan.
//
// Fix: the render loop compares the projection matrix each frame.
// When the camera moved → fadeOpacity=0 → trail clears instantly.
// When the camera is still → fadeOpacity=0.996 → long beautiful trails.
// Trails rebuild in ~1 second after panning stops.
//
// This is geographically correct: particles always reflect the actual
// wind at their UV position. The trails just don't persist through pans.

const DECODE_PARTICLE = /* glsl */ `
precision highp float;
vec2 decodeParticle(vec4 color) {
  // R = coarse (floor(x*255)/255), G = fine (fract(x*255)).
  // Correct decode: coarse + fine/255.  The previous version had
  // r/255 + g which put the division on the wrong channel, causing
  // every particle to decode to a garbage UV position and immediately
  // fail the viewport bounds check - hence the constant spawn-blink loop.
  return vec2(
    color.r + color.g / 255.0,
    color.b + color.a / 255.0
  );
}
`;

export const QUAD_VS = /* glsl */ `
precision highp float;
attribute vec2 a_pos;
varying vec2 v_tex_pos;
void main() {
  v_tex_pos = a_pos;
  // Standard NDC mapping - no x-flip. An earlier version used
  // (1 - 2*a_pos) which mirrored the trail horizontally vs the
  // geo-projected particles, making trails appear scrambled.
  gl_Position = vec4(2.0 * a_pos - 1.0, 0.0, 1.0);
}
`;

export const SCREEN_FS = /* glsl */ `
precision highp float;
uniform sampler2D u_screen;
uniform float u_opacity;
varying vec2 v_tex_pos;
void main() {
  vec4 c = texture2D(u_screen, v_tex_pos);
  // Fade RGB and alpha at the SAME rate (squared) — previously RGB used
  // u_opacity while alpha used u_opacity^2, so RGB outlasted alpha and
  // left visible 'snail trails' on the framebuffer after particles faded.
  float fade = u_opacity * u_opacity;
  // FIX for "moth-eaten trails" GPU-quantization bug (2026-05-28):
  //
  // The trail buffer is UNSIGNED_BYTE (8-bit per channel). Each fade pass
  // does: read 8-bit → multiply fade (~0.970) → write 8-bit. With
  // round-to-nearest the value can get permanently stuck:
  //
  //   5/255 × 0.970 × 255 = 4.86 → rounds back to 5 → STUCK FOREVER
  //
  // Any integer n ≤ ~16 satisfies round(n×0.970) = n, so all values in
  // [1..16]/255 form a quantization floor below which the pixel never
  // decays. The snap threshold (0.0045 ≈ 1.15/255) sits below this
  // floor, so it never fires — leaving a low-alpha haze on the map
  // indefinitely (the "moth-eaten" appearance).
  //
  // Fix (a): subtract a small epsilon AFTER multiplying so every frame
  // produces a strictly smaller value. 1.5/255 is just above the GPU
  // rounding error: round(n×fade − 1.5) < n for all n ≥ 1.
  //
  // Fix (b): raise the snap threshold to 0.025 (~6/255) as belt-and-
  // suspenders — any sub-visible haze is cleared aggressively even if
  // epsilon alone leaves a 1–2 quantum remnant.
  const float EPSILON = 1.5 / 255.0;
  float a = max(0.0, c.a   * fade - EPSILON);
  vec3 rgb = max(vec3(0.0), c.rgb * fade - EPSILON);
  // Raised snap threshold: 0.025 ≈ 6/255. Kills the quantization-stuck
  // band (was 0.0045 ≈ 1.15/255 which sat BELOW the stuck floor ~16/255).
  if (a < 0.025 && max(rgb.r, max(rgb.g, rgb.b)) < 0.025) {
    gl_FragColor = vec4(0.0);
  } else {
    gl_FragColor = vec4(rgb, a);
  }
}
`;

// UPDATE: advect particles one step along the wind field.
// When u_viewport_uv contains a valid viewport rectangle (width/height > 0.001),
// retired particles respawn within that viewport and out-of-bounds particles are
// instantly retired.  If the uniform was not set (defaults to vec4(0)) the viewport
// check is skipped and particles respawn globally - safe fallback.
export const UPDATE_FS = /* glsl */ `
precision highp float;
uniform sampler2D u_particles;
uniform sampler2D u_wind;
uniform vec2 u_wind_res;
uniform vec2 u_wind_min;
uniform vec2 u_wind_max;
uniform float u_rand_seed;
uniform float u_speed_factor;
uniform float u_drop_rate;
uniform float u_drop_rate_bump;
uniform vec4 u_viewport_uv;   // minX, minY, maxX, maxY in UV [0,1]2; zero = disabled
varying vec2 v_tex_pos;
${DECODE_PARTICLE}
vec4 encodeParticle(vec2 pos) {
  vec2 c = clamp(pos, 0.0, 1.0);
  return vec4(
    floor(c.x*255.0)/255.0, fract(c.x*255.0),
    floor(c.y*255.0)/255.0, fract(c.y*255.0)
  );
}
float rand(vec2 co) { return fract(sin(dot(co, vec2(12.9898,78.233)))*43758.5453); }
vec2 lookupWind(vec2 uv) {
  // GFS data from cfgrib is stored S→N (row 0 = 90°S). Our UV y=0 = 90°N,
  // so flip y when sampling the texture.
  vec2 texUV = vec2(uv.x, 1.0 - uv.y);
  vec2 px = 1.0/u_wind_res, vc = floor(texUV*u_wind_res)*px, f = fract(texUV*u_wind_res);
  return mix(u_wind_min, u_wind_max,
    mix(mix(texture2D(u_wind,vc).rg,           texture2D(u_wind,vc+vec2(px.x,0)).rg, f.x),
        mix(texture2D(u_wind,vc+vec2(0,px.y)).rg, texture2D(u_wind,vc+px).rg,        f.x), f.y));
}
void main() {
  vec2 pos = decodeParticle(texture2D(u_particles, v_tex_pos));

  // Small spatial jitter when sampling wind: prevents particles from lining up
  // along GFS grid-cell boundaries (visible as faint dividing lines when zoomed).
  // ±0.9 px of jitter is imperceptible to wind direction accuracy but breaks alignment.
  vec2 jitter = (vec2(rand(pos + u_rand_seed * 0.4), rand(pos.yx + u_rand_seed * 0.6)) - 0.5)
                * 1.8 / u_wind_res;
  vec2 vel = lookupWind(pos + jitter);

  // Zoom-adaptive speed: u_speed_factor is calibrated for z8 (vw≈0.015 UV).
  // At higher zoom (smaller vw) each UV unit = more screen pixels - scale
  // speed down so apparent motion is consistent across zoom levels.
  float vw = max(u_viewport_uv.z - u_viewport_uv.x, 0.001);
  float zoomScale = clamp(vw / 0.015, 0.05, 4.0);

  // Non-linear speed scaling so slow winds look noticeably slower on screen.
  // Applies a gentle power curve (^1.3) anchored at 100 m/s = 1:1, so:
  //   5 m/s → ~40% of linear speed   (calm looks calm)
  //  20 m/s → ~65% of linear speed
  //  60 m/s → ~94% of linear speed   (strong winds barely affected)
  float vel_mag = length(vel);
  float visual_mag = vel_mag > 0.001 ? pow(vel_mag, 1.3) / pow(100.0, 0.3) : 0.0;
  vec2 vel_visual = vel_mag > 0.001 ? (vel / vel_mag) * visual_mag : vec2(0.0);

  pos = fract(1.0 + pos + vec2(vel_visual.x, -vel_visual.y) * 0.00008 * u_speed_factor * zoomScale);

  vec2 seed = (pos + v_tex_pos) * u_rand_seed;

  // Calm-zone dropout: particles in near-zero wind accumulate because they barely
  // move.  Boost drop probability in calm areas so they respawn quickly.
  // Uses smoothstep (not a hard cliff) to avoid a visible density line at the
  // transition speed — previously the linear ramp to 8 m/s left a faint seam.
  float speed_ms = vel_mag;
  float calm_boost = (1.0 - smoothstep(0.0, 14.0, speed_ms)) * 0.010;
  float drop = step(1.0 - (u_drop_rate + calm_boost), rand(seed));

  // Viewport constraint - only active when the uniform describes a real rectangle.
  float vh = u_viewport_uv.w - u_viewport_uv.y;
  float vpValid = step(0.001, vw) * step(0.001, vh);  // 1 if valid, 0 if not

  // Retire particles outside the viewport, but with a 6% margin so particles
  // fade naturally before hitting the hard UV edge (avoids alignment artefacts
  // at viewport boundaries when zoomed in and stationary).
  float mx = vw * 0.06;
  float my = vh * 0.06;
  float ox = step(pos.x, u_viewport_uv.x - mx) + step(u_viewport_uv.z + mx, pos.x);
  float oy = step(pos.y, u_viewport_uv.y - my) + step(u_viewport_uv.w + my, pos.y);
  drop = clamp(drop + vpValid * clamp(ox + oy, 0.0, 1.0), 0.0, 1.0);

  // Respawn: within viewport when valid, globally otherwise.
  vec2 vpSpawn = vec2(
    u_viewport_uv.x + rand(seed + 1.3) * max(vw, 0.001),
    u_viewport_uv.y + rand(seed + 2.1) * max(vh, 0.001)
  );
  vec2 globalSpawn = vec2(rand(seed + 1.3), rand(seed + 2.1));
  vec2 spawnPos = mix(globalSpawn, vpSpawn, vpValid);
  spawnPos = clamp(spawnPos, 0.001, 0.999);  // safety: never spawn at exact UV boundary

  pos = mix(pos, spawnPos, drop);
  gl_FragColor = encodeParticle(pos);
}
`;

// DRAW: project each particle to screen as a round point.
// We use MapLibre's `projectTile()` (injected via shaderData.vertexShaderPrelude
// at compile time) so that particles wrap correctly onto the globe in globe-
// projection mode and clip at the horizon — instead of floating past the
// sphere's edge into black space.  __PRELUDE__ is replaced at link time by
// the WindParticleLayer with the current projection prelude string.
//
// IMPORTANT: MapLibre's prelude already declares
//     const float PI = 3.141592653589793;
//     uniform mat4 u_projection_matrix;
// so this shader MUST NOT redeclare PI (collision = compile failure =
// silent loss of particles). Use the prelude's PI directly.  We also
// can't put a top-level `precision` directive here because the prelude
// will be prepended before it and some drivers reject precision after
// other declarations. Vertex shaders default to highp in WebGL1, which
// is fine for what we do.
export const DRAW_VS = /* glsl */ `
__PRELUDE__
attribute float a_index;
uniform sampler2D u_particles;
uniform sampler2D u_wind;
uniform vec2 u_wind_min;
uniform vec2 u_wind_max;
uniform float u_particles_res;
varying float v_speed_norm;
${DECODE_PARTICLE}
void main() {
  vec2 pos = decodeParticle(texture2D(u_particles, vec2(
    fract(a_index / u_particles_res),
    floor(a_index / u_particles_res) / u_particles_res
  )));
  vec2 vel = mix(u_wind_min, u_wind_max, texture2D(u_wind, pos).rg);
  v_speed_norm = clamp(length(vel) / 60.0, 0.0, 1.0);
  // UV → Mercator XY in [0,1]² world space (PI comes from the prelude).
  float lat = clamp(90.0 - pos.y * 180.0, -85.0, 85.0);
  float sin_lat = sin(radians(lat));
  float merc_y = 0.5 - 0.25 * log((1.0 + sin_lat) / (1.0 - sin_lat)) / PI;
  // projectTile() handles globe/mercator transparently and clips at the
  // horizon — particles on the far side of the sphere are culled.
  gl_Position  = projectTile(vec2(pos.x, merc_y));
  gl_PointSize = 9.0 + v_speed_norm * 6.0;  // 9–15 px — more visible
}
`;

export const DRAW_FS = /* glsl */ `
precision highp float;
varying float v_speed_norm;
void main() {
  vec2 d = gl_PointCoord - vec2(0.5);
  float r = length(d);
  if (r > 0.5) discard;
  float edge = smoothstep(0.5, 0.15, r);
  // White → orange: stands out against the VFR sectional.
  // Avoid cyan/blue — blends with water, class B/C airspace on the chart.
  vec3 slow = vec3(1.00, 1.00, 1.00);  // white = calm
  vec3 fast = vec3(1.00, 0.50, 0.05);  // orange = strong wind
  vec3 color = mix(slow, fast, smoothstep(0.1, 0.8, v_speed_norm));
  gl_FragColor = vec4(color, edge * (0.92 + 0.08 * v_speed_norm));
}
`;
